import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Clock, Repeat, X } from 'lucide-react';
import { Program } from '../data/mockData';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { parseApiDate } from '../utils/dateTime';

interface AvailabilityCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  programs: Program[];
}

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function AvailabilityCalendarModal({
  isOpen,
  onClose,
  programs,
}: AvailabilityCalendarModalProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days: Date[] = [];
    let day = calStart;
    while (day <= calEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  const programsByDay = useMemo(() => {
    const map = new Map<string, Program[]>();
    const addToDay = (key: string, p: Program) => {
      const list = map.get(key) ?? [];
      if (!list.some((x) => String(x.id) === String(p.id))) {
        list.push(p);
      }
      map.set(key, list);
    };
    for (const program of programs) {
      try {
        const start = parseApiDate(program.startDateTime);
        const end = parseApiDate(program.endDateTime);

        // Add to start date
        addToDay(format(start, 'yyyy-MM-dd'), program);

        // If end date is on a different day, add to every day it spans
        let cursor = addDays(startOfDay(start), 1);
        const endDay = startOfDay(end);
        while (cursor <= endDay) {
          addToDay(format(cursor, 'yyyy-MM-dd'), program);
          cursor = addDays(cursor, 1);
        }
      } catch {
        // skip invalid dates
      }
    }
    map.forEach((dayPrograms, key) => {
      map.set(
        key,
        dayPrograms.sort(
          (a: Program, b: Program) =>
            parseApiDate(a.startDateTime).getTime() -
            parseApiDate(b.startDateTime).getTime()
        )
      );
    });
    return map;
  }, [programs]);

  const today = new Date();
  const selectedPrograms = selectedDate ? programsByDay.get(selectedDate) || [] : [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { setSelectedDate(null); onClose(); } }}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-7xl w-[97vw] max-h-[95vh] overflow-y-auto p-0">
        <DialogHeader className="px-8 pt-8 pb-0">
          <DialogTitle className="text-2xl font-bold text-white">
            Check Availability
          </DialogTitle>
          <p className="text-sm text-gray-400 mt-1">Click a day to see scheduled program details.</p>
        </DialogHeader>

        <div className="px-8 pb-8 pt-5">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
              className="text-gray-400 hover:text-white hover:bg-gray-700 h-9 w-9 p-0"
            >
              <ChevronLeft className="size-5" />
            </Button>
            <h3 className="text-lg font-bold text-white">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
              className="text-gray-400 hover:text-white hover:bg-gray-700 h-9 w-9 p-0"
            >
              <ChevronRight className="size-5" />
            </Button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-2">
            {WEEK_DAYS.map((d) => (
              <div key={d} className="text-center text-sm font-semibold text-gray-400 py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 border border-gray-700 rounded-xl overflow-hidden">
            {calendarDays.map((day, idx) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayPrograms = programsByDay.get(dateKey) || [];
              const inMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, today);
              const hasPrograms = dayPrograms.length > 0;

              return (
                <div
                  key={idx}
                  onClick={() => hasPrograms && setSelectedDate(dateKey)}
                  className={`min-h-[100px] border border-gray-700/40 p-2 ${
                    inMonth ? 'bg-gray-800/80' : 'bg-gray-800/20'
                  } ${isToday ? 'ring-2 ring-blue-500/50 bg-blue-950/20' : ''} ${
                    hasPrograms ? 'cursor-pointer hover:bg-gray-700/50' : ''
                  } transition-colors`}
                >
                  <div
                    className={`text-sm font-semibold mb-1 ${
                      isToday
                        ? 'text-blue-400'
                        : inMonth
                          ? 'text-gray-200'
                          : 'text-gray-600'
                    }`}
                  >
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    {dayPrograms.slice(0, 2).map((p) => {
                      const pStart = format(parseApiDate(p.startDateTime), 'HH:mm');
                      const pEnd = format(parseApiDate(p.endDateTime), 'HH:mm');
                      return (
                        <div
                          key={p.id}
                          className="bg-blue-600/20 text-blue-200 rounded-md px-1.5 py-1"
                        >
                          <div className="text-[10px] font-medium leading-tight truncate flex items-center gap-0.5">
                            {p.scheduleType === 'recurring' && <Repeat className="size-2.5 text-purple-400 shrink-0" />}
                            {p.title}
                          </div>
                          <div className="text-[9px] text-blue-300/70 leading-tight">
                            {pStart} – {pEnd}
                          </div>
                        </div>
                      );
                    })}
                    {dayPrograms.length > 2 && (
                      <span className="text-[10px] text-gray-400 pl-0.5 font-medium">
                        +{dayPrograms.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Detail Popup */}
        {selectedDate && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedDate(null)}
          >
            <div
              className="bg-gray-850 border border-gray-600/50 rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[75vh] overflow-y-auto"
              style={{ backgroundColor: '#1a1f2e' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-700/50">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {format(new Date(selectedDate + 'T00:00:00'), 'EEEE, MMMM dd, yyyy')}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">{selectedPrograms.length} program{selectedPrograms.length !== 1 ? 's' : ''} scheduled</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDate(null)}
                  className="text-gray-400 hover:text-white hover:bg-gray-700 h-8 w-8 p-0 rounded-full"
                >
                  <X className="size-4" />
                </Button>
              </div>
              <div className="px-6 py-5 space-y-4">
                {selectedPrograms.map((p) => {
                  const start = parseApiDate(p.startDateTime);
                  const end = parseApiDate(p.endDateTime);
                  return (
                    <div
                      key={p.id}
                      className="bg-gray-900/60 border border-gray-700/50 rounded-xl p-5"
                    >
                      <h4 className="text-base font-semibold text-white mb-2.5">{p.title}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <Clock className="size-4 text-blue-400 shrink-0" />
                        <span>{format(start, 'hh:mm a')} – {format(end, 'hh:mm a')}</span>
                      </div>
                      {p.description && (
                        <p className="text-sm text-gray-400 mt-3 leading-relaxed line-clamp-4">
                          {p.description}
                        </p>
                      )}
                      {p.scheduleType === 'recurring' && (
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          <span className="inline-flex items-center gap-1 text-xs text-purple-400 bg-purple-500/10 border border-purple-500/20 rounded-full px-2 py-0.5">
                            <Repeat className="size-3" /> Recurring
                          </span>
                          {p.recurrence && (
                            <span className="text-xs text-gray-400">
                              {p.recurrence.frequency === 'daily' ? 'Daily' : 'Weekly'}
                              {p.recurrence.frequency === 'weekly' && p.recurrence.daysOfWeek && p.recurrence.daysOfWeek.length > 0 &&
                                ` · ${p.recurrence.daysOfWeek.slice().sort((a: number, b: number) => a - b).map((d: number) => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ')}`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
